import React, { useState, useEffect } from 'react';
import FormInput from '../../../components/common/FormInput/FormInput';
import FormTextarea from '../../../components/common/FormTextarea/FormTextarea';
import FormSelect from '../../../components/common/FormSelect/FormSelect';
import Logo from '../../../components/common/Logo/Logo';
import RadioPillGroup from '../../../components/common/RadioPillGroup/RadioPillGroup';
import AlertBanner from '../../../components/common/AlertBanner/AlertBanner';
import Button from '../../../components/common/Button/Button';
import { LeadService } from '../../../services';
import { CreateLeadPayload } from '../../../types';
import { maskPhone, maskInstagram, maskDate, unmaskPhone } from '../../../utils/masks';
import { validateEmail, validatePhone } from '../../../utils/validation';
import styles from './LeadForm.module.css';

const DRAFT_STORAGE_KEY = 'marimosca_lead_draft';

const MONTHLY_INCOME_OPTIONS = [
  { value: 'Até R$2.000', label: 'Até R$2.000' },
  { value: 'R$2.001 – R$4.000', label: 'R$2.001 – R$4.000' },
  { value: 'R$4.001 – R$8.000', label: 'R$4.001 – R$8.000' },
  { value: 'R$8.001 – R$15.000', label: 'R$8.001 – R$15.000' },
  { value: 'Acima de R$15.000', label: 'Acima de R$15.000' },
  { value: 'Prefiro não informar', label: 'Prefiro não informar' },
];

const SCORE_OPTIONS = Array.from({ length: 11 }, (_, i) => String(i));

interface LeadFormState {
  full_name: string;
  whatsapp_phone: string;
  email: string;
  instagram_handle: string;
  birth_date: string;
  training_location: string;
  currently_training: string;
  satisfied_with_results: string;
  main_pain_point: string;
  main_goal: string;
  has_injury_or_limitation: string;
  injury_details: string;
  had_professional_coaching: string;
  coaching_worked: string;
  body_priority_score: string;
  can_afford_plan: string;
  expectations: string;
  referral_source: string;
  referred_by: string;
  social_follow_time: string;
  monthly_income_range: string;
}

const EMPTY_FORM: LeadFormState = {
  full_name: '',
  whatsapp_phone: '',
  email: '',
  instagram_handle: '',
  birth_date: '',
  training_location: '',
  currently_training: '',
  satisfied_with_results: '',
  main_pain_point: '',
  main_goal: '',
  has_injury_or_limitation: '',
  injury_details: '',
  had_professional_coaching: '',
  coaching_worked: '',
  body_priority_score: '',
  can_afford_plan: '',
  expectations: '',
  referral_source: '',
  referred_by: '',
  social_follow_time: '',
  monthly_income_range: '',
};

interface FormErrors {
  full_name?: string;
  whatsapp_phone?: string;
  email?: string;
}

const parseBool = (value: string): boolean | null => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

const parseDate = (value: string): string | null => {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  return `${year}-${month}-${day}`;
};

const YES_NO_OPTIONS = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
];

const LeadForm: React.FC = () => {
  const [formData, setFormData] = useState<LeadFormState>(() => {
    try {
      const saved = sessionStorage.getItem(DRAFT_STORAGE_KEY);
      return saved ? { ...EMPTY_FORM, ...JSON.parse(saved) } : EMPTY_FORM;
    } catch {
      return EMPTY_FORM;
    }
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  const setField = (field: keyof LeadFormState, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Nome completo é obrigatório.';
    }

    const phoneError = validatePhone(unmaskPhone(formData.whatsapp_phone));
    if (phoneError) newErrors.whatsapp_phone = phoneError;

    const emailError = validateEmail(formData.email.trim());
    if (emailError) newErrors.email = emailError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) return;

    setIsSubmitting(true);

    const payload: CreateLeadPayload = {
      full_name: formData.full_name.trim(),
      whatsapp_phone: unmaskPhone(formData.whatsapp_phone),
      email: formData.email.trim(),
      instagram_handle: formData.instagram_handle.trim() || null,
      birth_date: parseDate(formData.birth_date),
      training_location: formData.training_location.trim() || null,
      currently_training: parseBool(formData.currently_training),
      satisfied_with_results: parseBool(formData.satisfied_with_results),
      main_pain_point: formData.main_pain_point.trim() || null,
      main_goal: formData.main_goal.trim() || null,
      has_injury_or_limitation: parseBool(formData.has_injury_or_limitation),
      injury_details: formData.injury_details.trim() || null,
      had_professional_coaching: parseBool(formData.had_professional_coaching),
      coaching_worked: parseBool(formData.coaching_worked),
      body_priority_score: formData.body_priority_score !== '' ? parseInt(formData.body_priority_score, 10) : null,
      can_afford_plan: parseBool(formData.can_afford_plan),
      expectations: formData.expectations.trim() || null,
      referral_source: formData.referral_source.trim() || null,
      referred_by: formData.referred_by.trim() || null,
      social_follow_time: formData.social_follow_time.trim() || null,
      monthly_income_range: formData.monthly_income_range || null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_term: null,
      utm_content: null,
    };

    try {
      await LeadService.create(payload);
      sessionStorage.removeItem(DRAFT_STORAGE_KEY);
      setIsSuccess(true);
    } catch {
      setSubmitError('Algo deu errado. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles['success-wrap']}>
            <span className={`material-symbols-outlined ${styles['success-icon']}`}>check_circle</span>
            <h2 className={styles['success-title']}>Formulário enviado!</h2>
            <p className={styles['success-message']}>
              Obrigada por preencher o formulário. Entraremos em contato em breve!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <Logo />
          <h1 className={styles.title}>Formulário de Aplicação</h1>
          <p className={styles.subtitle}>
            Preencha as informações abaixo para dar o primeiro passo na sua transformação.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <p className={styles['section-title']}>Dados pessoais</p>

          <FormInput
            id="full_name"
            label="Nome completo"
            required
            value={formData.full_name}
            onChange={(e) => setField('full_name', e.target.value)}
            error={errors.full_name}
            placeholder="Seu nome completo"
            disabled={isSubmitting}
          />

          <FormInput
            id="whatsapp_phone"
            label="WhatsApp com DDD"
            required
            value={formData.whatsapp_phone}
            onChange={(e) => setField('whatsapp_phone', maskPhone(e.target.value))}
            error={errors.whatsapp_phone}
            placeholder="(00) 00000-0000"
            inputMode="tel"
            disabled={isSubmitting}
          />

          <FormInput
            id="email"
            label="Melhor e-mail"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setField('email', e.target.value)}
            error={errors.email}
            placeholder="seu@email.com"
            disabled={isSubmitting}
          />

          <FormInput
            id="instagram_handle"
            label="Instagram (se tiver)"
            value={formData.instagram_handle}
            onChange={(e) => setField('instagram_handle', maskInstagram(e.target.value))}
            placeholder="@seu.perfil"
            disabled={isSubmitting}
          />

          <FormInput
            id="birth_date"
            label="Data de nascimento"
            value={formData.birth_date}
            onChange={(e) => setField('birth_date', maskDate(e.target.value))}
            placeholder="dd/mm/aaaa"
            inputMode="numeric"
            disabled={isSubmitting}
          />

          <p className={styles['section-title']}>Sobre seu treino</p>

          <FormInput
            id="training_location"
            label="Onde você pretende treinar?"
            value={formData.training_location}
            onChange={(e) => setField('training_location', e.target.value)}
            placeholder="Academia, em casa, ao ar livre..."
            disabled={isSubmitting}
          />

          <div className={styles['field-group']}>
            <label className={styles['field-label']}>Você está treinando atualmente?</label>
            <RadioPillGroup
              options={YES_NO_OPTIONS}
              value={formData.currently_training}
              onChange={(v) => setField('currently_training', v)}
              disabled={isSubmitting}
            />
          </div>

          <div className={styles['field-group']}>
            <label className={styles['field-label']}>Está satisfeita com seus resultados?</label>
            <RadioPillGroup
              options={YES_NO_OPTIONS}
              value={formData.satisfied_with_results}
              onChange={(v) => setField('satisfied_with_results', v)}
              disabled={isSubmitting}
            />
          </div>

          <FormTextarea
            id="main_pain_point"
            label="Qual a sua maior dor ou dificuldade com o corpo hoje?"
            value={formData.main_pain_point}
            onChange={(e) => setField('main_pain_point', e.target.value)}
            rows={3}
            disabled={isSubmitting}
          />

          <FormTextarea
            id="main_goal"
            label="O que você mais deseja conquistar?"
            value={formData.main_goal}
            onChange={(e) => setField('main_goal', e.target.value)}
            rows={3}
            disabled={isSubmitting}
          />

          <div className={styles['field-group']}>
            <label className={styles['field-label']}>Você tem alguma limitação ou lesão que atrapalhe seu treino hoje?</label>
            <RadioPillGroup
              options={YES_NO_OPTIONS}
              value={formData.has_injury_or_limitation}
              onChange={(v) => setField('has_injury_or_limitation', v)}
              disabled={isSubmitting}
            />
          </div>

          {formData.has_injury_or_limitation === 'true' && (
            <FormTextarea
              id="injury_details"
              label="Me explica melhor"
              value={formData.injury_details}
              onChange={(e) => setField('injury_details', e.target.value)}
              rows={3}
              disabled={isSubmitting}
            />
          )}

          <p className={styles['section-title']}>Experiência anterior</p>

          <div className={styles['field-group']}>
            <label className={styles['field-label']}>Já teve acompanhamento profissional (personal ou consultoria online)?</label>
            <RadioPillGroup
              options={YES_NO_OPTIONS}
              value={formData.had_professional_coaching}
              onChange={(v) => setField('had_professional_coaching', v)}
              disabled={isSubmitting}
            />
          </div>

          {formData.had_professional_coaching === 'true' && (
            <div className={styles['field-group']}>
              <label className={styles['field-label']}>Funcionou pra você? Sentiu que foi levada a sério?</label>
              <RadioPillGroup
                options={YES_NO_OPTIONS}
                value={formData.coaching_worked}
                onChange={(v) => setField('coaching_worked', v)}
                disabled={isSubmitting}
              />
            </div>
          )}

          <p className={styles['section-title']}>Prioridades e expectativas</p>

          <div className={styles['field-group']}>
            <label className={styles['field-label']}>De 0 a 10, quanto o seu corpo é prioridade hoje na sua rotina?</label>
            <RadioPillGroup
              options={SCORE_OPTIONS}
              value={formData.body_priority_score}
              onChange={(v) => setField('body_priority_score', v)}
              disabled={isSubmitting}
              wrap
            />
          </div>

          <div className={styles['field-group']}>
            <label className={styles['field-label']}>A consultoria tem planos a partir de R$297/mês. Esse valor é possível pra você hoje?</label>
            <RadioPillGroup
              options={YES_NO_OPTIONS}
              value={formData.can_afford_plan}
              onChange={(v) => setField('can_afford_plan', v)}
              disabled={isSubmitting}
            />
          </div>

          <FormTextarea
            id="expectations"
            label="O que você espera da consultoria?"
            value={formData.expectations}
            onChange={(e) => setField('expectations', e.target.value)}
            rows={3}
            disabled={isSubmitting}
          />

          <p className={styles['section-title']}>Como nos encontrou</p>

          <FormInput
            id="referral_source"
            label="Como você me conheceu?"
            value={formData.referral_source}
            onChange={(e) => setField('referral_source', e.target.value)}
            placeholder="Instagram, indicação, YouTube..."
            disabled={isSubmitting}
          />

          <FormInput
            id="referred_by"
            label="Se foi indicada por alguém, pode me contar quem foi?"
            value={formData.referred_by}
            onChange={(e) => setField('referred_by', e.target.value)}
            disabled={isSubmitting}
          />

          <FormInput
            id="social_follow_time"
            label="Me acompanha nas redes sociais há quanto tempo?"
            value={formData.social_follow_time}
            onChange={(e) => setField('social_follow_time', e.target.value)}
            placeholder="Ex: 6 meses, 1 ano..."
            disabled={isSubmitting}
          />

          <p className={styles['section-title']}>Informação opcional e sigilosa</p>

          <p className={styles['section-note']}>
            Essa pergunta existe para que eu consiga te orientar de forma justa e indicar planos ou materiais que caibam na sua realidade.
            Essa informação não influencia sua seleção.
          </p>

          <FormSelect
            id="monthly_income_range"
            label="Faixa de renda familiar mensal"
            options={MONTHLY_INCOME_OPTIONS}
            value={formData.monthly_income_range}
            onChange={(e) => setField('monthly_income_range', e.target.value)}
            placeholder="Selecione..."
            disabled={isSubmitting}
          />

          {submitError && (
            <AlertBanner message={submitError} variant="error" />
          )}

          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            className={styles['submit-btn']}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Enviando...' : 'Enviar formulário'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default LeadForm;
